/**
 * Type guards for Ory API response detection.
 * 
 * Distinguishes between flows, error envelopes, and redirect responses for proper error handling.
 */
import type {
  OryKratosFlow,
  OryErrorEnvelope,
  OryErrorBrowserRedirect,
  OryErrorContinueWith,
  OryContinueWithAction,
} from '../model';

/**
 * Type guard to check if response is an Ory browser redirect error.
 *
 * Detects error responses requiring browser location change (flow expired, replaced, etc.).
 * Mirrors Ory SDK's `ErrorBrowserLocationChangeRequired`.
 *
 * Handles three Ory redirect formats:
 * 1. Top-level: `redirect_browser_to` (older format)
 * 2. Nested: `error.details.redirect_to` (newer format, seen in 410 Gone responses)
 * 3. Flow ID: `use_flow_id` (newest format - provides new flow ID instead of URL)
 */
export const isErrorBrowserRedirect = (data: unknown): data is OryErrorBrowserRedirect => {
  if (data === null || typeof data !== 'object') {
    return false;
  }

  // Check for top-level use_flow_id (newest format)
  if (
    Object.prototype.hasOwnProperty.call(data, 'use_flow_id')
    && typeof (data as { use_flow_id: unknown }).use_flow_id === 'string'
    && (data as { use_flow_id: string }).use_flow_id.length > 0
  ) {
    return true;
  }

  // Check for top-level redirect_browser_to (older format)
  if (
    Object.prototype.hasOwnProperty.call(data, 'redirect_browser_to')
    && typeof (data as { redirect_browser_to: unknown }).redirect_browser_to === 'string'
  ) {
    return true;
  }

  // Check for nested error.details.redirect_to (newer format)
  if (Object.prototype.hasOwnProperty.call(data, 'error')) {
    const error = (data as { error: unknown }).error;
    if (error && typeof error === 'object' && Object.prototype.hasOwnProperty.call(error, 'details')) {
      const details = (error as { details: unknown }).details;
      if (details && typeof details === 'object' && Object.prototype.hasOwnProperty.call(details, 'redirect_to')) {
        const redirectTo = (details as { redirect_to: unknown }).redirect_to;
        return typeof redirectTo === 'string' && redirectTo.length > 0;
      }
    }
  }

  return false;
};

/**
 * Type guard to check if response is an Ory error envelope.
 *
 * Detects Ory error envelopes by checking for the `error` object with
 * at least one of the standard Ory error fields (message, reason, status, code).
 * Note: Ory doesn't always include the `id` field (e.g., session/whoami 401).
 */
export const isErrorEnvelope = (data: unknown): data is OryErrorEnvelope => {
  if (data === null || typeof data !== 'object') {
    return false;
  }

  if (!Object.prototype.hasOwnProperty.call(data, 'error')) {
    return false;
  }

  const error = (data as { error: unknown }).error;

  if (error === null || typeof error !== 'object') {
    return false;
  }

  // Check for at least one standard Ory error field
  // (Ory doesn't always provide 'id', e.g., session 401 responses)
  return (
    Object.prototype.hasOwnProperty.call(error, 'message')
    || Object.prototype.hasOwnProperty.call(error, 'reason')
    || Object.prototype.hasOwnProperty.call(error, 'status')
    || Object.prototype.hasOwnProperty.call(error, 'code')
  );
};

/**
 * Type guard to check if response data is a Kratos flow object.
 *
 * Ory flows have:
 * - id: string (flow UUID)
 * - type: 'browser' | 'api'
 * - ui: { nodes, messages, action, method }
 *
 * Note: This includes both stateful flows (login, registration, etc.)
 * and logout flows (which only have logout_token/logout_url)
 */
export const isFlowResponse = (data: unknown): data is OryKratosFlow => {
  if (data === null || typeof data !== 'object') {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(data, 'id')
    && typeof (data as { id: unknown }).id === 'string'
    && Object.prototype.hasOwnProperty.call(data, 'type')
  );
};

/**
 * Actions in continue_with that require browser navigation.
 * 
 * These actions indicate Ory wants the browser to navigate to a flow UI:
 * - show_verification_ui: User needs to verify email/phone
 * - show_settings_ui: User needs to update settings (e.g., after MFA setup)
 * - show_recovery_ui: User needs to complete recovery flow
 */
const CONTINUE_WITH_REDIRECT_ACTIONS: OryContinueWithAction[] = [
  'show_verification_ui',
  'show_settings_ui', 
  'show_recovery_ui',
];

/**
 * Type guard to check if response is an Ory error with continue_with UI actions.
 * 
 * Detects Ory error responses where the error contains a `details.continue_with` array
 * with UI actions that require browser navigation (show_*_ui actions with flow URLs).
 * 
 * Common scenarios:
 * - `session_verified_address_required`: Login blocked, user must verify email
 * - Settings flow completion requiring verification
 * 
 * @example
 * ```typescript
 * // Error response from login with unverified email
 * const response = {
 *   error: {
 *     id: 'session_verified_address_required',
 *     details: {
 *       continue_with: [{
 *         action: 'show_verification_ui',
 *         flow: { id: '...', url: '/verification?flow=...' }
 *       }]
 *     }
 *   }
 * };
 * 
 * if (isErrorContinueWith(response)) {
 *   // TypeScript knows response is OryErrorContinueWith
 *   const url = response.error.details?.continue_with?.[0]?.flow.url;
 * }
 * ```
 */
export const isErrorContinueWith = (data: unknown): data is OryErrorContinueWith => {
  if (data === null || typeof data !== 'object') {
    return false;
  }

  // Must have error object
  if (!Object.prototype.hasOwnProperty.call(data, 'error')) {
    return false;
  }

  const error = (data as { error: unknown }).error;
  if (error === null || typeof error !== 'object') {
    return false;
  }

  // Must have details with continue_with array
  if (!Object.prototype.hasOwnProperty.call(error, 'details')) {
    return false;
  }

  const details = (error as { details: unknown }).details;
  if (details === null || typeof details !== 'object') {
    return false;
  }

  if (!Object.prototype.hasOwnProperty.call(details, 'continue_with')) {
    return false;
  }

  const continueWith = (details as { continue_with: unknown }).continue_with;
  if (!Array.isArray(continueWith) || continueWith.length === 0) {
    return false;
  }

  // Check if any action is a redirect action with a valid flow URL
  return continueWith.some((action: unknown) => {
    if (action === null || typeof action !== 'object') {
      return false;
    }

    const actionObj = action as { action?: unknown; flow?: unknown };
    
    // Must be a redirect action type
    if (typeof actionObj.action !== 'string' || 
        !CONTINUE_WITH_REDIRECT_ACTIONS.includes(actionObj.action as OryContinueWithAction)) {
      return false;
    }

    // Must have flow with URL
    if (actionObj.flow === null || typeof actionObj.flow !== 'object') {
      return false;
    }

    const flow = actionObj.flow as { url?: unknown };
    return typeof flow.url === 'string' && flow.url.length > 0;
  });
};


